// Onaylanmamış önemli duyurular için kalıcı şerit — tüm panellerde, sayfanın en
// üstünde (akış içinde; panellerin yapışkan başlığı kaydırınca üste yapışır).
// "Okudum" denene kadar kalır; birden fazlaysa sırayla (1/3). Ana sayfada yok (kart
// gösteriyor); Canlı Pano, sipariş takip / takip linki ve tablette de yok.
import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, ChevronDown, Check } from "lucide-react";
import { getSession, isSessionValid, isTabletSession } from "../../lib/auth";
import { stripAllowedOn, pendingImportant, formatDateTime } from "../../lib/announcements";
import useAnnouncementFeed from "./useAnnouncementFeed";

const currentSession = () => (isSessionValid() ? getSession() : null);

export default function GlobalAnnouncementStrip() {
  const location = useLocation();
  const [session, setSession] = useState(currentSession);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const handler = () => setSession(currentSession());
    window.addEventListener("auth-changed", handler);
    window.addEventListener("storage", handler);
    return () => {
      window.removeEventListener("auth-changed", handler);
      window.removeEventListener("storage", handler);
    };
  }, []);
  useEffect(() => { setSession(currentSession()); }, [location.pathname]);

  const enabled = !!session && !isTabletSession() && stripAllowedOn(location.pathname);
  const { items, markSeen, ack } = useAnnouncementFeed(enabled);
  const pending = enabled ? pendingImportant(items) : [];
  const current = pending[0];

  useEffect(() => { if (current) markSeen([current]); }, [current, markSeen]);
  useEffect(() => { setOpen(false); }, [current?.id]);

  if (!current) return null;

  const onAck = async () => {
    setBusy(true);
    try {
      await ack(current.id);
      toast.success("Okundu olarak işaretlendi");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "İşaretlenemedi, tekrar deneyin");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div role="alert" className="relative z-30 border-b-2 border-warning bg-warning/15 text-text-primary" data-testid="announcement-strip">
      <div className="mx-auto max-w-7xl px-3 py-2 sm:px-4 flex items-start gap-2 sm:gap-3">
        <AlertTriangle className="h-5 w-5 text-warning shrink-0 mt-0.5" aria-hidden="true" />
        <div className="min-w-0 flex-1">
          <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open}
            className="w-full text-left flex flex-wrap sm:flex-nowrap items-center gap-x-2 gap-y-0.5 min-w-0" data-testid="announcement-strip-toggle">
            <span className="text-[11px] font-bold uppercase tracking-wide text-warning shrink-0">
              Önemli duyuru{pending.length > 1 ? ` 1/${pending.length}` : ""}
            </span>
            <ChevronDown className={`h-4 w-4 shrink-0 text-text-secondary transition-transform sm:order-last ${open ? "rotate-180" : ""}`} aria-hidden="true" />
            {/* telefonda açıkken başlık etiketin altına iner (dar sütunda kelime bölünmesin) */}
            <span className={`font-semibold text-sm min-w-0 ${open ? "break-words basis-full sm:basis-auto" : "truncate flex-1 sm:flex-initial"}`} data-testid="announcement-strip-title">{current.title}</span>
          </button>
          {open && (
            <div className="pt-1" data-testid="announcement-strip-body">
              <p className="text-sm whitespace-pre-line break-words">{current.body}</p>
              <p className="text-[11px] text-text-secondary mt-1">
                {current.created_by_name} · {formatDateTime(current.updated_at || current.created_at)}
              </p>
            </div>
          )}
        </div>
        <button type="button" onClick={onAck} disabled={busy}
          className="shrink-0 inline-flex items-center gap-1 rounded-md bg-warning px-3 py-1.5 text-sm font-bold text-black hover:bg-warning/90 disabled:opacity-60"
          data-testid="announcement-strip-ack">
          <Check className="h-4 w-4" aria-hidden="true" /> Okudum
        </button>
      </div>
    </div>
  );
}
